import "./SongLink.css";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { track as trackEvent } from "../../utils/analytics";
import { FaPlay, FaPause, FaStop, FaAmazon } from "react-icons/fa";
import {
  SiSpotify,
  SiApplemusic,
  SiYoutubemusic,
  SiYoutube,
  SiTiktok,
} from "react-icons/si";
import { FiArrowUpRight, FiCheck, FiEye, FiInfo, FiShare, FiX } from "react-icons/fi";
import SocialLinks from "../../components/SocialLinks/SocialLinks";
import { SONGS } from "../../data/songs";
import { ARTIST_NAME } from "../../data/artist";
import { PEOPLE } from "../../data/people";
import useScrollMemory from "../../utils/useScrollMemory";
import usePreviewPlayer from "../../utils/usePreviewPlayer";
import SiteLogo from "../../components/SiteLogo/SiteLogo";
import ScrollIndicator from "../../components/ScrollIndicator/ScrollIndicator";
import { isCollection, releaseMeta } from "../../utils/releaseTracks";
import Copyright from "../../components/Copyright/Copyright";
import useOverlay from "../../utils/useOverlay";
import {
  outLabel,
  isHidden,
  isTeaser,
  stageOf,
  TEASER_PATH,
  currentTeaserKey,
} from "../../utils/releaseStatus";

// The iTunes Store app's icon, in its own colors: a pink-to-purple rounded
// square with the star cut out (see-through, like the other logos' cut-outs).
// (Simple Icons only has the old iTunes ring.)
// Grey like the others while the buttons are off (see .is-disabled in CSS).
function ItunesStoreIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" {...props}>
      <defs>
        <linearGradient id="itunes-store-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F657C8" />
          <stop offset="1" stopColor="#A543F2" />
        </linearGradient>
      </defs>
      {/* The square with the star cut out: the page shows through the star */}
      <path fill="url(#itunes-store-gradient)" fillRule="evenodd" d="M5.5 0h13A5.5 5.5 0 0 1 24 5.5v13a5.5 5.5 0 0 1-5.5 5.5h-13A5.5 5.5 0 0 1 0 18.5v-13A5.5 5.5 0 0 1 5.5 0z M12.00 5.40 L13.76 10.17 L18.85 10.38 L14.85 13.53 L16.23 18.42 L12.00 15.60 L7.77 18.42 L9.15 13.53 L5.15 10.38 L10.24 10.17Z" />
    </svg>
  );
}

// Display order and labels for the streaming buttons
// color: the platform's logo color (its current official shade): the icon,
//   and its glow on hover
// cutout: the shape of the logo's see-through part (the play button, the
//   waves, the note), lightly darkened so it doesn't look lit (PlatformLogo)
// action: the word on its button on a single's page ("Play" if left out)
const PLATFORMS = [
  { key: "youtube", name: "YouTube", Icon: SiYoutube, color: "#FF0033", cutout: "box" },
  {
    key: "youtubeMusic",
    name: "YouTube Music",
    Icon: SiYoutubemusic,
    color: "#FF0033",
    cutout: "circle",
  },
  { key: "spotify", name: "Spotify", Icon: SiSpotify, color: "#1ED760", cutout: "circle" },
  {
    key: "appleMusic",
    name: "Apple Music",
    Icon: SiApplemusic,
    color: "#FA2D48",
    cutout: "square",
  },
  // To buy the song: its button says "Download" instead of "Play"
  {
    key: "itunes",
    name: "iTunes Store",
    Icon: ItunesStoreIcon,
    color: "#C94EEA", // (for its glow on hover)
    cutout: "square",
    action: "Download",
  },
  // The song as a TikTok sound (white: TikTok's black wouldn't show here)
  { key: "tiktok", name: "TikTok", Icon: SiTiktok, color: "#FFFFFF" },
  {
    key: "amazonMusic",
    name: "Amazon Music",
    Icon: FaAmazon,
    color: "#25D1DA",
  },
];

// A platform's logo: the icon, with a faint dark tint behind its see-through
// part (still see-through, just a little darker)
function PlatformLogo({ Icon, cutout, className = "" }) {
  if (!cutout) return <Icon className={className} aria-hidden="true" />;
  return (
    <span className={`platform-logo is-${cutout} ${className}`} aria-hidden="true">
      <Icon />
    </span>
  );
}

const DEFAULT_TITLE = "thangcao"; // matches <title> in public/index.html

// Play/pause control for the song's preview clip (audio engine in usePreviewPlayer)
function PreviewButton({ preview, levelTargetRef, slug }) {
  const player = usePreviewPlayer({ levelTargetRef });
  const playing = player.playingId === "song";
  const progress = player.currentId === "song" ? player.progress : 0;

  return (
    <div className="song-preview">
      <button
        className="song-preview-button"
        onClick={
          playing
            ? player.pause
            : () => {
                player.play(preview, "song");
                trackEvent("preview_play", { song: slug });
              }
        }
        aria-label={playing ? "Pause preview" : "Play preview"}
      >
        {playing ? <FaPause /> : <FaPlay className="song-preview-play-icon" />}
      </button>
      <div className="song-preview-track">
        <span className="song-preview-label">
          {playing ? "Playing preview" : "Preview"}
        </span>
        <div className="song-preview-bar">
          <div
            className="song-preview-bar-fill"
            style={{ transform: `scaleX(${progress})` }}
          />
        </div>
      </div>
    </div>
  );
}

const MARQUEE_GAP = 40; // px between the end of a long title and its repeat
const MARQUEE_SPEED = 40; // px per second

/*
 * A track's title. If it's too long for the row, it fades out at the edge,
 * and hovering the row (or playing the song) rolls it to the left in a
 * seamless loop so the whole name can be read.
 */
function TrackTitle({ title }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0); // 0 = fits, no rolling needed

  useEffect(() => {
    const box = boxRef.current;
    const text = textRef.current;
    const measure = () => {
      const width = text.scrollWidth;
      setTextWidth(width > box.clientWidth + 1 ? width : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [title]);

  const distance = textWidth + MARQUEE_GAP;
  return (
    <span
      ref={boxRef}
      className={`song-track-title ${textWidth ? "is-long" : ""}`}
      style={
        textWidth
          ? {
              "--marquee-distance": `${distance}px`,
              "--marquee-time": `${(distance / MARQUEE_SPEED).toFixed(2)}s`,
              "--marquee-gap": `${MARQUEE_GAP}px`,
            }
          : undefined
      }
    >
      <span className="song-track-marquee">
        <span ref={textRef}>{title}</span>
        {/* A second copy follows the first, so the loop has no jump */}
        {textWidth ? <span aria-hidden="true">{title}</span> : null}
      </span>
    </span>
  );
}

/*
 * EP/album songs, each with its own preview. Tap a song to play its preview
 * (tap again to pause); when one ends, the next song's preview follows.
 *
 * Songs with their own artwork show it as a thumbnail, and the release page
 * can show it big in place of the release cover (see SongLink):
 *   - hovering a song (mouse) shows its art for as long as it's hovered
 *   - choosing a song (click/tap) keeps its art up; choosing it again goes
 *     back to the release cover. Playing a song's preview chooses it too.
 * `shownTrack` is the chosen song; `onChoose(i | null)` / `onHover(i | null)`
 * report changes.
 */
function Tracklist({
  tracks,
  levelTargetRef,
  slug,
  shownTrack,
  onChoose,
  onHover,
  showEye = true,
  stopRef,
  onLoadedChange,
}) {
  const player = usePreviewPlayer({ levelTargetRef });

  // Let the page stop the preview (STOP, or × under the cover), and know
  // whether one is loaded (to show STOP)
  if (stopRef) stopRef.current = player.stop;
  const anyLoaded = player.currentId !== null;
  useEffect(() => {
    onLoadedChange?.(anyLoaded);
  }, [anyLoaded, onLoadedChange]);

  const playTrack = (i) => {
    const next = tracks.findIndex((track, j) => j > i && track.preview?.src);
    onChoose(i);
    player.play(tracks[i].preview, `track-${i}`, {
      onEnd: next === -1 ? undefined : () => playTrack(next),
    });
  };

  const hover = (i) => (event) => {
    if (event.pointerType === "mouse") onHover(i);
  };

  return (
    <ol className="song-tracks" aria-label="Songs" onPointerLeave={hover(null)}>
      {tracks.map((track, i) => {
        const id = `track-${i}`;
        const playable = Boolean(track.preview?.src);
        const playing = player.playingId === id;
        const loaded = player.currentId === id;
        const chosen = shownTrack === i;
        // Clickable when there's a preview to play or artwork to show
        const usable = playable || Boolean(track.artwork);
        const choose = (event) => {
          if (playable) {
            if (playing) return player.pause();
            playTrack(i);
            trackEvent("preview_play", { song: slug, track: track.title });
            return undefined;
          }
          onChoose(chosen ? null : i, event);
          return undefined;
        };
        return (
          <li
            key={`${i}-${track.title}`}
            className={`song-track ${playable ? "has-preview" : ""} ${loaded ? "is-loaded" : ""} ${playing ? "is-playing" : ""} ${
              chosen ? "is-chosen" : ""
            }`}
          >
            {/* The small artwork on its own: shows this song's art on the
                cover without playing (tap again to go back). Laid over the
                thumbnail inside the row below. */}
            {playable && track.artwork ? (
              <button
                type="button"
                className="song-track-thumb-button"
                onClick={() => onChoose(chosen && !playing ? null : i)}
                onPointerEnter={hover(i)}
                aria-label={`${track.title}: show its artwork`}
                aria-pressed={chosen}
                data-cursor="View"
              />
            ) : null}
            <button
              className="song-track-button"
              disabled={!usable}
              onClick={choose}
              onPointerEnter={track.artwork ? hover(i) : undefined}
              aria-pressed={!playable && track.artwork ? chosen : undefined}
              aria-label={
                playable
                  ? `${playing ? "Pause" : "Play"} preview of ${track.title}`
                  : track.artwork
                    ? `${track.title}: show its artwork`
                    : `${track.title} (no preview)`
              }
            >
              <span className="song-track-lead" aria-hidden="true">
                <span className="song-track-number">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {playable ? (
                  <span className="song-track-icon">
                    {playing ? (
                      <>
                        {/* Playing: moving bars; pause on hover */}
                        <span className="song-track-bars">
                          <i />
                          <i />
                          <i />
                        </span>
                        <FaPause className="song-track-pause-icon" />
                      </>
                    ) : (
                      <FaPlay className="song-track-play-icon" />
                    )}
                  </span>
                ) : null}
              </span>
              {track.artwork ? (
                <img
                  className="song-track-thumb"
                  src={track.artwork}
                  alt=""
                  aria-hidden="true"
                />
              ) : null}
              <TrackTitle title={track.title} />
              {/* An eye while this song's art is up on the cover (before
                  release only; once out, the playing bars show the song) */}
              {showEye && track.artwork ? (
                <span className="song-track-eye" aria-hidden="true">
                  <FiEye />
                </span>
              ) : null}
            </button>
            <div className="song-track-bar" aria-hidden="true">
              <div
                style={{ transform: `scaleX(${loaded ? player.progress : 0})` }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/*
 * Phones: opens the system share sheet. Desktop (or if sharing fails):
 * copies the link and briefly shows "Link copied".
 */
function ShareButton({ title, text, url, slug }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers: copy through a temporary text field
      const field = document.createElement("textarea");
      field.value = url;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      document.execCommand("copy");
      field.remove();
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    const isTouch = window.matchMedia?.("(pointer: coarse)").matches;
    if (navigator.share && isTouch) {
      try {
        await navigator.share({ title, text, url });
        trackEvent("share", { method: "share_sheet", song: slug });
        return;
      } catch (error) {
        if (error.name === "AbortError") return; // closed the share sheet
      }
    }
    copyLink();
    trackEvent("share", { method: "copy_link", song: slug });
  };

  return (
    <button className="song-share" onClick={share} aria-live="polite">
      {copied ? <FiCheck /> : <FiShare />}
      <span>{copied ? "Link copied" : "Share"}</span>
    </button>
  );
}

/*
 * The artwork, big: opens when the cover is clicked. Shows whatever the cover
 * shows at that moment (an EP song's own art, or the release cover). Closes
 * with a click anywhere, the × button or Escape; the page behind doesn't
 * scroll meanwhile (useOverlay).
 */
// A release's (or song's) credits as [role, who] pairs, leaving out empty ones
const creditRows = (credits) =>
  Object.entries(credits || {}).filter(([, who]) => who && who.length);

/*
 * Credits on the cover: a small round "i" in its corner ("CREDITS" shows next
 * to it on hover). Clicking or tapping it lays the credits over a blurred copy
 * of the art, until closed again on the cover (the icon's ×, or a click on the
 * credits; Escape too). Clicks elsewhere on the page leave them open, and
 * clicking the rest of the cover still opens it large. `credits` comes from
 * data/songs.js: each role and who did it, e.g.
 * { "Composed by": "thangcao", "Mixed & mastered by": ["A", "B"] }. On an
 * EP/album, the hovered or chosen song's own `credits` are shown instead, if
 * it has them (with its art behind them).
 */
function CoverCredits({ credits, art, slug, open, onToggle, onClose, buttonRef }) {
  const rows = creditRows(credits);
  if (!rows.length) return null;

  return (
    <div
      className={`song-cover-credits ${open ? "is-open" : ""}`}
      // Clicks here are about the credits, not "open the cover large"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div
        className="song-cover-credits-panel"
        id={`song-credits-${slug}`}
        aria-hidden={!open}
        data-cursor="Close"
        onClick={onClose}
      >
        <img className="song-cover-credits-blur" src={art} alt="" />
        <dl className="song-credits-list">
          {rows.map(([role, who]) => (
            <div className="song-credit" key={role}>
              <dt>{role}</dt>
              <dd>
                {/* Each name stays on one line; lines break between names */}
                {[].concat(who).map((name, i, names) => (
                  <span className="song-credit-name" key={name}>
                    {/* Linked when data/people.js has a link for this name */}
                    {PEOPLE[name] ? (
                      <a
                        href={PEOPLE[name]}
                        target="_blank"
                        rel="noopener noreferrer"
                        tabIndex={open ? undefined : -1}
                        onClick={(event) => {
                          event.stopPropagation(); // don't close the credits
                          trackEvent("credit_click", { song: slug, person: name });
                        }}
                      >
                        {name}
                      </a>
                    ) : (
                      name
                    )}
                    {i < names.length - 1 ? "," : ""}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <button
        ref={buttonRef}
        type="button"
        className="song-cover-credits-pill"
        aria-expanded={open}
        aria-controls={`song-credits-${slug}`}
        aria-label={open ? "Close credits" : "Credits"}
        data-cursor-hide
        onClick={onToggle}
      >
        {open ? <FiX aria-hidden="true" /> : <FiInfo aria-hidden="true" />}
        <span className="song-cover-credits-tip" aria-hidden="true">
          Credits
        </span>
      </button>
    </div>
  );
}

function ArtworkViewer({ src, alt, caption, onClose }) {
  const closeRef = useRef(null);
  useOverlay(true, onClose, closeRef);

  return createPortal(
    <div
      className="song-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        className="song-viewer-close"
        onClick={onClose}
        aria-label="Close"
      >
        <FiX />
      </button>
      <figure className="song-viewer-figure">
        <img className="song-viewer-image" src={src} alt={alt} />
        {caption ? (
          <figcaption className="song-viewer-caption">{caption}</figcaption>
        ) : null}
      </figure>
    </div>,
    document.body,
  );
}

function SongLink() {
  const { songSlug } = useParams();
  const address = songSlug?.toLowerCase();
  // The COMING SOON teaser lives at /soon (TEASER_PATH); `slug` is always the
  // release's key in songs.js
  const teaserKey = currentTeaserKey(SONGS);
  const slug = address === TEASER_PATH && teaserKey ? teaserKey : address;
  // Hidden releases (stage "hidden") act as if they weren't there, and so
  // does a teaser at its real address (that would give its title away)
  const onSite = (key) =>
    SONGS[key] && !isHidden(SONGS[key]) && (!isTeaser(SONGS[key]) || (key === teaserKey && address === TEASER_PATH));
  const song = onSite(slug) ? SONGS[slug] : null;
  // An old address (e.g. /soon, once its teaser is revealed) that now belongs
  // to another release
  const movedTo = song
    ? null
    : Object.keys(SONGS).find(
        (key) =>
          SONGS[key].aliases?.includes(address) &&
          !isHidden(SONGS[key]) &&
          !isTeaser(SONGS[key]),
      );
  const pageRef = useRef(null);
  const artworkRef = useRef(null);
  // EP/album: the song whose own artwork is shown on the cover (chosen with
  // a click/tap, or just hovered); null = the release cover
  const [chosenTrack, setChosenTrack] = useState(null);
  const [hoveredTrack, setHoveredTrack] = useState(null);
  const [viewing, setViewing] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const stopPreviewRef = useRef(null); // stops the EP/album track preview
  const [previewLoaded, setPreviewLoaded] = useState(false);
  // Clear the preview: stop it and go back to the release's cover
  const clearPreview = () => {
    stopPreviewRef.current?.();
    setChosenTrack(null);
    setHoveredTrack(null);
  };
  const creditsButtonRef = useRef(null); // the artwork, big (ArtworkViewer)
  // A different release: back to its own cover
  useEffect(() => {
    setCreditsOpen(false);
    setChosenTrack(null);
    setHoveredTrack(null);
  }, [slug]);

  // With a mouse, clicking anywhere outside the track list goes back to the
  // release cover. Not on touch screens: there every scroll starts with a
  // touch, which would make the art vanish while just scrolling; tapping the
  // song again (or another one) changes it instead.
  useEffect(() => {
    if (chosenTrack === null) return undefined;
    const onPointerDown = (event) => {
      if (event.pointerType !== "mouse") return;
      // (the cover and the big view keep it: they're showing that art)
      if (
        !event.target.closest?.(
          ".song-tracks, .song-artwork-wrap, .song-viewer",
        )
      )
        setChosenTrack(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [chosenTrack]);

  // Credits open: Escape closes them (back to the "i" button). Each page
  // view's first look is counted once.
  const creditsSeen = useRef(false);
  useEffect(() => {
    creditsSeen.current = false;
  }, [slug]);
  useEffect(() => {
    if (!creditsOpen) return undefined;
    if (!creditsSeen.current) {
      creditsSeen.current = true;
      trackEvent("credits_open", { song: slug });
    }
    const onKey = (event) => {
      if (event.key === "Escape") {
        setCreditsOpen(false);
        creditsButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [creditsOpen, slug]);

  useScrollMemory();

  const location = useLocation();
  // Arriving from a release card on the home page: the cover glides into place
  // (browser View Transitions), so skip the artwork's own fade-in.
  const morphingIn =
    Boolean(location.state?.morph) &&
    typeof document !== "undefined" &&
    "startViewTransition" in document;

  // "teaser", "announced" or "released" (see utils/releaseStatus.js)
  const stage = song ? stageOf(song) : null;
  const teaser = stage === "teaser";
  const announced = stage === "announced";

  useEffect(() => {
    if (!song) return;
    // Same format as <title> in scripts/generate-song-previews.js
    document.title = teaser
      ? `Coming soon · ${ARTIST_NAME}`
      : `${song.title} · ${song.artist}`;
    return () => {
      // Back to the site default when leaving the song page
      document.title = DEFAULT_TITLE;
    };
  }, [song, teaser]);

  if (movedTo) return <Navigate to={`/${movedTo}`} replace />;
  if (!song) return <Navigate to="/" replace />;

  // Teaser: no buttons. Announced: the buttons (all of them if there are no
  // links yet) are shown switched off, with the release date.
  const platforms = teaser
    ? []
    : announced && !Object.values(song.links || {}).some(Boolean)
      ? PLATFORMS
      : PLATFORMS.filter(({ key }) => song.links?.[key]);
  const collection = !teaser && isCollection(song); // EP/album with a track list
  const shownTrack = collection ? (hoveredTrack ?? chosenTrack) : null;

  // Choosing a song shows its art on the cover (the page stays where it is)
  const chooseTrack = (i) => setChosenTrack(i);
  const cover = teaser ? song.teaserArtwork : song.artwork;
  // What the cover shows right now: an EP song's own art, or the cover itself
  const shownArt =
    shownTrack !== null ? song.tracks[shownTrack].artwork : cover;
  const shownCaption = teaser
    ? "Coming soon"
    : shownTrack !== null
      ? `${String(shownTrack + 1).padStart(2, "0")} · ${song.tracks[shownTrack].title}`
      : `${song.title} · ${song.artist}`;
  // Announced: the date shows once, next to the type ("Single · Coming
  // 20.11"); the buttons just say "Coming soon"
  const meta = announced
    ? `${song.type} · ${outLabel(song)}`
    : releaseMeta(song);

  return (
    <div className="song-page" ref={pageRef}>
      <div
        className="song-backdrop"
        style={{ backgroundImage: `url(${cover})` }}
        aria-hidden="true"
      />
      {/* EP/album: each song's art as a backdrop too, faded in with it */}
      {collection
        ? song.tracks.map((track, i) =>
            track.artwork ? (
              <div
                key={i}
                className={`song-backdrop song-backdrop--track ${shownTrack === i ? "is-shown" : ""}`}
                style={{ backgroundImage: `url(${track.artwork})` }}
                aria-hidden="true"
              />
            ) : null,
          )
        : null}
      <div className="song-backdrop-shade" aria-hidden="true" />

      <header className="song-header">
        <SiteLogo to="/" />
      </header>

      <main className={`song-content ${teaser ? "is-teaser" : ""}`}>
        <div
          ref={artworkRef}
          className={`song-artwork-wrap ${
            morphingIn ? "song-artwork-wrap--morph" : ""
          } ${teaser ? "is-still" : ""}`}
          // Click (or Enter/Space) to see the artwork big; not for COMING
          // SOON, whose cover is only a blur
          {...(teaser
            ? {}
            : {
                role: "button",
                tabIndex: 0,
                "aria-label": "View the artwork larger",
                "data-cursor": "View",
                onClick: () => setViewing(true),
                onKeyDown: (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setViewing(true);
                  }
                },
              })}
        >
          <img
            className="song-artwork"
            src={cover}
            // A teaser's cover is already obscured in the file itself
            alt={teaser ? "New release, coming soon" : `${song.title} artwork`}
            // Same name as the home page's release card, so the cover glides between them
            style={{ viewTransitionName: `cover-${slug}` }}
          />
          {/* EP/album: each song's own artwork, laid over the cover and faded
              in while that song is chosen or hovered */}
          {collection
            ? song.tracks.map((track, i) =>
                track.artwork ? (
                  <img
                    key={i}
                    className={`song-artwork song-artwork--track ${shownTrack === i ? "is-shown" : ""}`}
                    src={track.artwork}
                    alt={shownTrack === i ? `${track.title} artwork` : ""}
                    aria-hidden={shownTrack === i ? undefined : "true"}
                  />
                ) : null,
              )
            : null}
          {!teaser && song.credits ? (
            <CoverCredits
              key={slug}
              // The shown EP/album song's own credits (hovered or chosen,
              // like its art), else the release's
              credits={(shownTrack !== null && song.tracks?.[shownTrack]?.credits) || song.credits}
              art={shownArt}
              slug={slug}
              open={creditsOpen}
              onToggle={() => setCreditsOpen((value) => !value)}
              onClose={() => setCreditsOpen(false)}
              buttonRef={creditsButtonRef}
            />
          ) : null}
          {/* Under the cover, once a song is chosen (not just hovered): which
              song's art this is, with a × back to the release cover */}
          {collection && chosenTrack !== null ? (
            <button
              type="button"
              className="song-artwork-label"
              onClick={(event) => {
                event.stopPropagation();
                clearPreview(); // back to the cover, and stops the preview
              }}
              onKeyDown={(event) => event.stopPropagation()}
              aria-label={`Showing ${song.tracks[chosenTrack].title}. Back to the ${song.type || "release"} cover`}
              data-cursor-hide
            >
              <span className="song-artwork-label-text">
                {String(chosenTrack + 1).padStart(2, "0")} ·{" "}
                {song.tracks[chosenTrack].title}
              </span>
              <FiX aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div className={`song-info ${teaser ? "is-teaser" : ""}`}>
          {teaser ? (
            // Teaser: only COMING SOON and the year
            <>
              <h1 className="song-title">Coming soon</h1>
              {song.year ? (
                <p className="song-meta">Arriving {song.year}</p>
              ) : null}
            </>
          ) : (
            <>
              <div className="song-meta-row">
                <p className="song-meta">{meta}</p>
                <ShareButton
                  title={`${song.title} · ${song.artist}`}
                  text={
                    collection
                      ? `Listen to the ${song.type || "album"} “${song.title}” by ${song.artist}`
                      : `Stream “${song.title}” by ${song.artist}`
                  }
                  url={`${window.location.origin}/${slug}`}
                  slug={slug}
                />
              </div>
              <h1 className="song-title">{song.title}</h1>
              <p className="song-artist">{song.artist}</p>
            </>
          )}

          {!teaser && !collection && song.preview?.src ? (
            <PreviewButton
              preview={song.preview}
              levelTargetRef={pageRef}
              slug={slug}
            />
          ) : null}

          {collection ? (
            // EP/album: "Stream now" and a compact row of icons above the
            // track list; hovering or focusing an icon shows the platform's name
            <div className="song-stream">
              <p className="song-stream-label" id="song-stream-label">
                {announced ? "Coming soon" : "Stream now"}
              </p>
              <nav
                className="song-platform-icons"
                aria-labelledby="song-stream-label"
              >
                {platforms.map(({ key, name, Icon, color, cutout }) =>
                  announced ? (
                    // Not out yet: the icon is shown, but isn't a link
                    <span
                      key={key}
                      className="song-platform-icon-link is-disabled"
                      aria-label={`${name}, coming soon`}
                      role="img"
                      style={{ "--brand": color }}
                    >
                      <Icon aria-hidden="true" />
                    </span>
                  ) : (
                    <a
                      key={key}
                      href={song.links[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Listen on ${name}`}
                      className="song-platform-icon-link"
                      style={{ "--brand": color }}
                      onClick={() =>
                        trackEvent("stream_click", {
                          song: slug,
                          platform: key,
                        })
                      }
                    >
                      <PlatformLogo Icon={Icon} cutout={cutout} />
                      <span className="song-platform-tip" aria-hidden="true">
                        {name}
                      </span>
                    </a>
                  ),
                )}
              </nav>
            </div>
          ) : platforms.length ? (
            <nav className="song-platforms" aria-label="Listen on">
              {platforms.map(({ key, name, Icon, color, cutout, action }) =>
                announced ? (
                  // Not out yet: shown with the date instead of "Play"
                  <div
                    key={key}
                    className="song-platform is-disabled"
                    aria-disabled="true"
                  >
                    <Icon className="song-platform-icon" />
                    <span className="song-platform-name">{name}</span>
                    <span className="song-platform-action">Coming soon</span>
                  </div>
                ) : (
                  <a
                    key={key}
                    className="song-platform"
                    href={song.links[key]}
                    style={{ "--brand": color }}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("stream_click", { song: slug, platform: key })
                    }
                  >
                    <PlatformLogo Icon={Icon} cutout={cutout} className="song-platform-icon" />
                    <span className="song-platform-name">{name}</span>
                    <span className="song-platform-action">
                      {action || "Play"} <FiArrowUpRight />
                    </span>
                  </a>
                ),
              )}
            </nav>
          ) : null}

          {/* EP/album: the track list, below the streaming icons, under a
              "Preview" heading when its songs have previews to play */}
          {collection ? (
            <div className="song-tracks-section">
              {song.tracks.some((track) => track.preview?.src) ? (
                <div className="song-tracks-head">
                  <p className="song-stream-label">Preview</p>
                  {/* While a preview is loaded: stop it and clear */}
                  {previewLoaded ? (
                    <button
                      type="button"
                      className="song-share song-preview-stop"
                      onClick={clearPreview}
                    >
                      <FaStop aria-hidden="true" />
                      <span>Stop</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
              <Tracklist
                stopRef={stopPreviewRef}
                onLoadedChange={setPreviewLoaded}
                tracks={song.tracks}
                levelTargetRef={pageRef}
                slug={slug}
                shownTrack={chosenTrack}
                onChoose={chooseTrack}
                onHover={setHoveredTrack}
                showEye={stage !== "released"}
              />
            </div>
          ) : null}

        </div>
      </main>

      {viewing ? (
        <ArtworkViewer
          src={shownArt}
          alt={teaser ? "New release, coming soon" : `${shownCaption} artwork`}
          caption={shownCaption}
          onClose={() => setViewing(false)}
        />
      ) : null}

      <ScrollIndicator />
      {/* Same follow icons as the home page: SoundCloud only on the Contact panel */}
      <SocialLinks
        exclude={["soundcloud"]}
        white
        music
        position="centered"
        fixed
        // The site's own accounts, not the song's credits (which may include a featuring)
        label={`follow ${ARTIST_NAME}`}
      />
      {/* The copyright at the very bottom, under the follow links */}
      <Copyright className="song-copyright" />
    </div>
  );
}

export default SongLink;
